// assets/product-custom-property.js
import { Component } from '@theme/component';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement | undefined} textInput - The text input.
 * @property {HTMLInputElement | undefined} fileInput - The file input.
 * @property {HTMLElement | undefined} characterCount - The character count element.
 */

/**
 * A custom element that manages product custom properties
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const productSection = this.closest('.shopify-section');
    productSection?.addEventListener('change', this.#handleVariantChange, { signal });

    this.#updateConditionalVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  handleInput() {
    this.#updateCharacterCount();
  }

  handleFileChange() {
    const fileInput = this.refs.fileInput;
    const file = fileInput?.files?.[0];
    if (!fileInput || !file) return;

    const maxSizeMb = Number(fileInput.dataset.maxFileSizeMb || 10);
    const maxSizeBytes = maxSizeMb * 1024 * 1024;
    const acceptedTypes = fileInput.accept
      .split(',')
      .map((type) => type.trim().toLowerCase())
      .filter(Boolean);

    if (file.size > maxSizeBytes) {
      fileInput.setCustomValidity(`Please choose an image smaller than ${maxSizeMb} MB.`);
      fileInput.reportValidity();
      return;
    }

    if (acceptedTypes.length > 0 && file.type && !acceptedTypes.includes(file.type.toLowerCase())) {
      fileInput.setCustomValidity('Please choose a JPG, PNG, or WEBP image.');
      fileInput.reportValidity();
      return;
    }

    fileInput.setCustomValidity('');
  }

  #handleVariantChange = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('variant-picker')) return;
    this.#updateConditionalVisibility();
  };

  #updateConditionalVisibility() {
    const field = /** @type {HTMLElement | null} */ (this.querySelector('[data-custom-property-field]'));
    if (!field) return;

    const conditionValue = field.dataset.showWhenVariantValue?.trim().toLowerCase();
    if (!conditionValue) return;

    const productSection = this.closest('.shopify-section');
    const selectedValues = Array.from(
      productSection?.querySelectorAll('variant-picker input[type="radio"]:checked, variant-picker select') || []
    ).map((control) => /** @type {HTMLInputElement | HTMLSelectElement} */ (control).value.trim().toLowerCase());

    const shouldShow = selectedValues.includes(conditionValue);
    const input = /** @type {HTMLInputElement | HTMLTextAreaElement | null} */ (
      field.querySelector('input, textarea')
    );

    field.hidden = !shouldShow;
    if (!input) return;

    input.disabled = !shouldShow;
    input.required = shouldShow && field.dataset.required === 'true';
    input.setAttribute('aria-required', input.required ? 'true' : 'false');
    if (!shouldShow) input.setCustomValidity('');
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    if (!characterCount || !textInput) return;
    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }
}

if (!customElements.get('product-custom-property-component')) {
  customElements.define('product-custom-property-component', ProductCustomProperty);
}
